/**
 * useInstallPrompt — captures the browser's `beforeinstallprompt` event
 * and provides a `promptInstall()` function.
 *
 * Also detects iOS Safari for manual "Add to Home Screen" instructions.
 *
 * Works on: Chrome, Edge, Samsung Internet, Opera, others with PWA support.
 * Safari (iOS/macOS) does NOT fire `beforeinstallprompt` — we detect it
 * and show manual instructions instead.
 */

import { useEffect, useState, useCallback } from 'react'

const DISMISSED_KEY = 'dukkan_pwa_dismissed'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true
  )
}

function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
}

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [installed, setInstalled] = useState(false)
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED_KEY) === '1'
    } catch {
      return false
    }
  })

  const showIosHint = isIOS() && !isStandalone()

  // Already running as installed PWA
  useEffect(() => {
    if (isStandalone()) setInstalled(true)
  }, [])

  // Capture the event
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }
    window.addEventListener('beforeinstallprompt', handler)

    // Detect when install completes
    const onInstalled = () => { setInstalled(true); setDeferredPrompt(null) }
    window.addEventListener('appinstalled', onInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handler)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  const canPrompt = deferredPrompt !== null && !installed
  const showBanner = (canPrompt || showIosHint) && !installed && !dismissed

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return false
    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    setDeferredPrompt(null)
    if (outcome === 'accepted') {
      setInstalled(true)
      return true
    }
    return false
  }, [deferredPrompt])

  const dismiss = useCallback(() => {
    setDismissed(true)
    try { localStorage.setItem(DISMISSED_KEY, '1') } catch {}
  }, [])

  return { canPrompt, showBanner, showIosHint, installed, promptInstall, dismiss }
}
